import React, { useMemo } from 'react';
import type { Role } from '@/types';
import { cn } from '@/lib/utils';
import { CustomizedFieldIndicator } from '../../form/CustomizedFieldIndicator';
import { getRoleId } from './utils';
import { fieldLabelClass, fieldDescriptionClass } from '@/components/config_forms/shared/surcharge/fieldStyles';

interface ApplicableRolesFieldProps {
  allRoles: Role[];
  selectedRoleIds: string[] | null;
  onSelectionChange: (roleIds: string[] | null) => void;
  isCustomized?: boolean;
  context: 'user' | 'client';
  showMobileDivider?: boolean;
  className?: string;
}

export const ApplicableRolesField: React.FC<ApplicableRolesFieldProps> = ({
  allRoles,
  selectedRoleIds,
  onSelectionChange,
  isCustomized,
  context,
  showMobileDivider = true,
  className,
}) => {
  const roleOptions = useMemo(() => {
    return allRoles.map((role, i) => ({
      id: getRoleId(role, i),
      name: role.name,
    }));
  }, [allRoles]);

  const roleRows = useMemo(() => {
    const count = roleOptions.length;
    if (count <= 3) return [roleOptions];

    // Greedy row balancing: prefer rows of 3, fall back to rows of 2.
    // Never leave a trailing singleton — redistribute instead.
    //   4  → [2,2]      5  → [3,2]      6  → [3,3]
    //   7  → [3,2,2]    8  → [3,3,2]    9  → [3,3,3]
    //   10 → [3,3,2,2]  11 → [3,3,3,2]  12 → [3,3,3,3]
    const bestPartition: number[] = (() => {
      if (count === 4) return [2, 2];
      const remainder = count % 3;
      if (remainder === 0) return new Array(count / 3).fill(3);
      if (remainder === 1) {
        // Replace the would-be-trailing "1" by downgrading one 3 → two 2s
        const threes = Math.floor((count - 4) / 3);
        return [...new Array(threes).fill(3), 2, 2];
      }
      // remainder === 2: all threes plus one trailing two
      const threes = Math.floor((count - 2) / 3);
      return [...new Array(threes).fill(3), 2];
    })();

    const rows: Array<Array<{ id: string; name: string }>> = [];
    let cursor = 0;
    for (const rowSize of bestPartition) {
      rows.push(roleOptions.slice(cursor, cursor + rowSize));
      cursor += rowSize;
    }
    return rows;
  }, [roleOptions]);

  const allRoleIds = useMemo(() => roleOptions.map(opt => opt.id), [roleOptions]);

  // null means "all roles" — no state write needed for the default
  const effectiveSelectedIds = selectedRoleIds ?? allRoleIds;

  // Don't render when there's only 1 role — toggling 1-of-1 is meaningless
  if (roleOptions.length <= 1) return null;

  const toggleRole = (roleId: string) => {
    const isSelected = effectiveSelectedIds.includes(roleId);
    if (isSelected) {
      // Prevent deselecting the last role
      if (effectiveSelectedIds.length <= 1) return;
      const remaining = effectiveSelectedIds.filter(id => id !== roleId);
      onSelectionChange(remaining);
    } else {
      const updated = [...effectiveSelectedIds, roleId];
      // If all roles are now selected, write null (canonical "all roles" value)
      onSelectionChange(updated.length >= allRoleIds.length ? null : updated);
    }
  };

  return (
    <div
      data-roles-field-row
      className={cn(
        "grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4 sm:flex sm:items-center sm:justify-between sm:gap-4",
        showMobileDivider ? "border-b border-border/40 pb-4 mb-1 sm:border-b-0 sm:pb-0 sm:mb-0" : "pb-0 mb-0",
        className,
      )}
    >
      <div className="min-w-0 space-y-1 self-start sm:self-center">
        <div className="flex items-center gap-2">
          <span className={fieldLabelClass}>
            Roles
          </span>
          <CustomizedFieldIndicator isCustomized={isCustomized} context={context} />
        </div>
        <p className={cn(fieldDescriptionClass, "md:max-w-[44ch]")}>
          Toggle a role to exclude it. The surcharge won't apply on shifts billed for disabled roles.
        </p>
      </div>
      <div
        className="min-w-0 w-full justify-self-end self-center flex flex-col items-end gap-2 sm:w-auto sm:max-w-[55%] sm:flex-row sm:flex-wrap sm:justify-end"
        role="group"
        aria-label="Applicable roles"
      >
        {roleRows.map((row, rowIndex) => (
          <div
            key={`role-row-${rowIndex}`}
            data-role-row
            data-roles-per-row={row.length}
            className="flex w-full min-w-0 justify-end gap-2 sm:contents"
          >
            {row.map(role => {
              const isActive = effectiveSelectedIds.includes(role.id);
              const isLastActive = isActive && effectiveSelectedIds.length <= 1;
              const shouldCenterMobileLabel = role.name.trim().length > 16;
              return (
                <button
                  key={role.id}
                  type="button"
                  role="checkbox"
                  aria-checked={isActive}
                  aria-disabled={isLastActive}
                  onClick={() => toggleRole(role.id)}
                  className={cn(
                    "relative inline-flex w-auto min-w-0 max-w-[11rem] sm:max-w-none !min-h-[34px] items-center gap-2 pl-2 pr-2 sm:px-2 py-1 sm:py-2 rounded-lg border text-xs font-medium",
                    "after:absolute after:inset-x-0 after:-inset-y-[5px] after:content-[''] sm:after:hidden",
                    "transition-[background-color,border-color,color,transform] duration-150 select-none",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
                    isActive
                      ? "bg-primary/15 border-primary/40 text-primary-text hover:bg-primary/25 hover:border-primary/55"
                      : "bg-transparent border-border text-muted-foreground hover:border-muted-foreground hover:text-foreground",
                    isLastActive && "cursor-not-allowed",
                    "active:translate-y-[1px]"
                  )}
                >
                  <span
                    className={cn(
                      "w-1.5 h-1.5 rounded-[1px] flex-shrink-0 transition-colors duration-150",
                      isActive
                        ? "bg-primary shadow-[0_0_4px_var(--primary)]"
                        : "bg-muted-foreground/50"
                    )}
                  />
                  <span
                    className={cn(
                      "min-w-0 break-normal whitespace-normal leading-tight sm:text-left",
                      shouldCenterMobileLabel ? "text-center" : "text-left",
                    )}
                  >
                    {role.name}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};
